import * as React from 'react'
import { MdAccountCircle as UserIcon } from '@react-icons/all-files/md/MdAccountCircle'
import { MdBusinessCenter } from '@react-icons/all-files/md/MdBusinessCenter'
import { MdGroup as UserGroupIcon } from '@react-icons/all-files/md/MdGroup'
import type { Recipient } from 'types/list'
import type { RecipientTypes } from '@disco/constants/RecipientTypes'
import {
  BUSINESS,
  EMAIL,
  RECIPIENT_GROUP,
  USER,
} from '@disco/constants/RecipientTypes'
/**
 * Recipient label component that displays recipient information with appropriate icons
 * Supports different recipient types (USER, BUSINESS, EMAIL, RECIPIENT_GROUP) with one-line or two-line layouts
 * @param recipient - Recipient object containing type, name, and email
 * @param appearance - Display mode: 'oneline' or 'twoline'
 */
export default class RecipientLabel extends React.Component<{
  recipient: Recipient
  appearance: 'oneline' | 'twoline'
}> {
  static defaultProps: {
    appearance: 'oneline' | 'twoline'
  } = {
    appearance: 'oneline',
  }

  _renderOneLine(
    type: RecipientTypes,
    name: string,
    email: string,
  ): React.ReactElement<'span'> {
    switch (type) {
      case USER:
        let label

        if (email && name) {
          label = `${name} <${email}>`
        } else {
          label = email || name || ''
        }

        return (
          <span>
            <UserIcon />
            {label}
          </span>
        )

      case BUSINESS:
        return (
          <span>
            <MdBusinessCenter />
            {name}
          </span>
        )

      case EMAIL:
        return (
          <span>
            <UserIcon />
            {email}
          </span>
        )

      case RECIPIENT_GROUP:
        return (
          <span>
            <UserGroupIcon />
            {name}
          </span>
        )

      default:
        return <span>{`[${type}] ${name}`}</span>
    }
  }

  _renderTwoLine(
    type: RecipientTypes,
    name: string,
    email: string,
  ): React.ReactElement<'div'> {
    let line1, line2, icon

    switch (type) {
      case USER:
        icon = <UserIcon />
        line1 = email
        line2 = name
        break

      case BUSINESS:
        icon = <MdBusinessCenter />
        line2 = name
        break

      case EMAIL:
        icon = <UserIcon />
        line2 = email
        break

      case RECIPIENT_GROUP:
        icon = <UserGroupIcon />
        line2 = name
        break

      default:
        icon = null
        line2 = <span>{`[${type}] ${name}`}</span>
        break
    }

    return (
      <div className="RecipientLabel flc">
        <div className="RecipientLabel_Icon">{icon}</div>
        <div className="RecipientLabel_Cont">
          <div className="RecipientLabel_Line1 overflow-ellipsis">{line1}</div>
          <div className="RecipientLabel_Line2 overflow-ellipsis">{line2}</div>
        </div>
      </div>
    )
  }

  render(): null | React.ReactElement<'div'> | React.ReactElement<'span'> {
    const { type } = this.props.recipient
    let name = this.props.recipient.name || ''
    let email = this.props.recipient.email || ''
    name = name.trim()
    email = email.trim()

    switch (this.props.appearance) {
      case 'oneline':
        return this._renderOneLine(type, name, email)

      case 'twoline':
        return this._renderTwoLine(type, name, email)

      default:
        return null
    }
  }
}
